import { FunctionComponent } from 'react';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * Maximum number of page-number slots to render, including any ellipsis
   * markers. Use a smaller value on narrow screens to keep the control on a
   * single line. The smallest useful layout (`[1, …, n, …, total]`) needs 5
   * slots, so values below 5 are clamped to 5. Defaults to 7.
   */
  maxVisible?: number;
  className?: string;
}

// The windowing layout always reserves the first page, last page, and any
// ellipses, so its structural floor is 5 slots (`[1, …, n, …, total]`).
const MIN_VISIBLE = 5;

export const Pagination: FunctionComponent<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisible = 7,
  className = '',
}) => {
  const getPageNumbers = (
    current: number,
    total: number,
    visible: number,
  ): (number | 'ellipsis')[] => {
    if (total <= 1) return total === 1 ? [1] : [];
    if (total <= visible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [1]; // always show first page
    const middleSlots = visible - 2; // slots available between first and last

    let leftEllipsis = current > 2;
    let rightEllipsis = current < total - 1;

    let numberCount =
      middleSlots - (leftEllipsis ? 1 : 0) - (rightEllipsis ? 1 : 0);
    if (numberCount < 1) numberCount = 1;

    let start = current - Math.floor((numberCount - 1) / 2);
    let end = start + numberCount - 1;
    if (start < 2) {
      start = 2;
      end = Math.min(total - 1, start + numberCount - 1);
    }
    if (end > total - 1) {
      end = total - 1;
      start = Math.max(2, end - numberCount + 1);
    }

    // re-evaluate ellipses after clamping the window to [2, total - 1]
    leftEllipsis = start > 2;
    rightEllipsis = end < total - 1;

    if (leftEllipsis) pages.push('ellipsis');
    for (let i = start; i <= end; i++) pages.push(i);
    if (rightEllipsis) pages.push('ellipsis');

    pages.push(total); // always show last page
    return pages;
  };

  // Clamp to the supported minimum so the documented slot cap always holds.
  const visible = Math.max(MIN_VISIBLE, maxVisible);
  const pageNumbers = getPageNumbers(currentPage, totalPages, visible);

  return (
    <nav
      aria-label="Pagination"
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-2 ${className}`}
    >
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="text-action-link hover:text-action-link-hover disabled:text-text-disabled text-body font-normal underline underline-offset-2 disabled:no-underline"
        aria-label="Go to previous page"
      >
        Previous
      </button>

      <div className="flex items-center gap-0.5 md:gap-1">
        {pageNumbers.map((page, index) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="text-action-link text-body flex h-9 min-w-9 items-center justify-center px-1 font-bold md:h-10 md:w-10"
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              disabled={currentPage === page}
              className={`text-body flex h-9 min-w-9 items-center justify-center rounded px-1 font-normal md:h-10 md:w-10 ${
                currentPage === page
                  ? 'bg-brand-primary text-white'
                  : 'text-action-link hover:text-action-link-hover underline underline-offset-2'
              }`}
              aria-label={`Go to page ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          ),
        )}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="text-action-link hover:text-action-link-hover disabled:text-text-disabled text-body font-normal underline underline-offset-2 disabled:no-underline"
        aria-label="Go to next page"
      >
        Next
      </button>
    </nav>
  );
};
